import type { MmSheetLayoutConfig } from './couponCardSizes';

export const A4_HEIGHT_MM = 297;

/** Matches `@page` vertical margin in `buildCouponA4PrintDocumentHtml` for mm-sized sheets. */
export const COUPON_A4_MM_PAGE_MARGIN_MM = 4;
/** Matches `.sheet-mm-wrap` bottom margin in print CSS. */
export const COUPON_A4_MM_WRAP_BOTTOM_MARGIN_MM = 3;
/** Allowance for body padding / print rounding so “max rows” is conservative. */
export const COUPON_A4_MM_PRINT_SAFETY_MM = 1.5;

export function gridWidthMm(config: MmSheetLayoutConfig): number {
  return config.cardWidthMm * config.cols + config.colGapMm * (config.cols - 1);
}

export function gridHeightMm(config: MmSheetLayoutConfig, rows: number): number {
  const r = Math.max(0, Math.floor(rows));
  if (r < 1) return 0;
  return r * config.cardHeightMm + (r - 1) * config.rowGapMm;
}

/** Vertical space available inside the page margin box for the grid + wrap bottom margin. */
export function printableHeightMmA4(): number {
  return (
    A4_HEIGHT_MM -
    2 * COUPON_A4_MM_PAGE_MARGIN_MM -
    COUPON_A4_MM_WRAP_BOTTOM_MARGIN_MM -
    COUPON_A4_MM_PRINT_SAFETY_MM
  );
}

export function maxA4Rows(config: MmSheetLayoutConfig): number {
  const printable = printableHeightMmA4();
  const cap = Math.floor(
    (printable + config.rowGapMm) / (config.cardHeightMm + config.rowGapMm)
  );
  return Math.max(1, cap);
}

export function clampA4Rows(config: MmSheetLayoutConfig, rows: number): number {
  const max = maxA4Rows(config);
  const n = Math.round(rows);
  if (!Number.isFinite(n)) return Math.min(config.defaultRows, max);
  return Math.min(max, Math.max(1, n));
}

/** Y positions (mm) for horizontal cut guides between row pairs. */
export function horizontalCutYsMm(config: MmSheetLayoutConfig, rows: number): number[] {
  const r = Math.floor(rows);
  if (r < 2) return [];
  return Array.from({ length: r - 1 }, (_, idx) => {
    const i = idx + 1;
    return i * config.cardHeightMm + (i - 1) * config.rowGapMm + config.rowGapMm / 2;
  });
}

export function couponsPerSheet(config: MmSheetLayoutConfig, rows: number): number {
  return config.cols * Math.max(1, Math.floor(rows));
}

/** X position (mm) for vertical cut guide between columns. */
export function verticalCutXMm(config: MmSheetLayoutConfig): number {
  return config.cardWidthMm + config.colGapMm / 2;
}
